/**
 * The demo backend: the API of plane-api.js, answered from memory, with the clawlets simulated.
 *
 * It exists so that the interface can be shown, and developed, without a backend or a server:
 * the `standalone` profile uses it (config-standalone.js). It follows the rules of the real plane
 * where the interface can see them:
 *
 * - "Apply" only changes the desired state and bumps the server's generation; the outcome arrives
 *   later, as the simulated clawlet acknowledges and reports the phases render → validate →
 *   secrets → write → reload → health → done, or rolls back;
 * - an offline server keeps its desired state, and applies it when it reconnects;
 * - the displayed state is derived by the backend's own rules (displayed-state.js);
 * - secrets are write-only and sealed: the demo drops the value, keeps the version, and the
 *   simulated clawlet reports holding it, as observed.secrets would.
 *
 * The data lives in sessionStorage, so a reload keeps the demo where it was; `demo.reset()` goes
 * back to fixtures.js. `demo.failNextApply(mode)` makes the next apply fail, to show a rollback.
 *
 * Shown as a customer (`demo.setViewAs`), it answers as the backend does to a customer user:
 * without the internals (server, digests, the clawlet's errors and messages), and refusing
 * a change of the parameters reserved to operators.
 */
import { ApiError } from '../api/index.js';
import { assistantIds, assistantSchema, operatorParams, operatorValues, validate } from '../params-form.js';
import { specialistsOf, teamErrors } from '../team.js';
import { displayedState } from './displayed-state.js';
import { IMAGE_VERSIONS, initialData } from './fixtures.js';

// v8: the pending pairing requests of the bots; an older demo saved in the tab has another shape
const STORAGE_KEY = 'plane-demo/v8';

/** The id of an assistant (protocol/schema/common.json, assistantId). */
const ASSISTANT_ID = /^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/;

/** The id a template proposes when its author names none (backend: TemplateService.assistantIdOf). */
const assistantIdOf = (name) => {
  const plain = name.normalize('NFKD').replace(/\p{M}/gu, '').toLowerCase().replace(/[^a-z0-9]+/g, '-');
  const id = plain.slice(0, 32).replace(/^-+|-+$/g, '');
  return id || 'assistant';
};

const checkedAssistantId = (id) => {
  if (!ASSISTANT_ID.test(id ?? '')) {
    throw new ApiError(400, 'ERR_INVALID', `the id of an assistant is 1 to 32 lowercase letters, digits and '-', starting and ending with a letter or a digit: ${id}`);
  }
  return id;
};

/** The name of a secret of an assistant (backend: ParamsSchemas.assistantSecretName). */
const assistantSecretName = (name, id) => `${name}__${id.replaceAll('-', '_').toUpperCase()}`;

/** What the clawlet requires at the head of a gateway name (backend: GatewayNames.PREFIX). */
const GATEWAY_PREFIX = 'openclaw-';

/** The ways the next apply can be made to fail. */
export const FAILURE_MODES = {
  validate: 'Validation fails: nothing is changed on the server',
  health: 'Health check fails: the clawlet rolls back to the previous revision',
  rollback: 'Health check fails and so does the rollback',
};

/** An IPv4 or IPv6 address, or a CIDR block of one: what the demo accepts as an expected network. */
const isNetwork = (text) => {
  const [address, prefix, ...rest] = text.split('/');
  if (rest.length > 0 || (prefix !== undefined && !/^\d{1,3}$/.test(prefix))) {
    return false;
  }
  if (ipv4(address) !== null) {
    return prefix === undefined || Number(prefix) <= 32;
  }
  return /^[0-9a-fA-F:]+$/.test(address) && address.includes(':') && (prefix === undefined || Number(prefix) <= 128);
};

const ipv4 = (text) => {
  const parts = text.split('.');
  if (parts.length !== 4 || parts.some((part) => !/^\d{1,3}$/.test(part) || Number(part) > 255)) {
    return null;
  }
  return parts.reduce((value, part) => value * 256 + Number(part), 0);
};

/** Whether an address is in one of the networks: IPv4 blocks by prefix, anything else by equality. */
const inNetworks = (networks, address) =>
  networks.some((network) => {
    const [base, prefix] = network.split('/');
    const value = ipv4(address);
    const start = ipv4(base);
    if (value === null || start === null || prefix === undefined) {
      return base === address;
    }
    const size = 2 ** (32 - Number(prefix));
    return Math.floor(value / size) === Math.floor(start / size);
  });

function sessionStore() {
  return {
    read() {
      try {
        const text = window.sessionStorage.getItem(STORAGE_KEY);
        return text ? JSON.parse(text) : null;
      } catch {
        return null;
      }
    },
    write(data) {
      try {
        window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
      } catch {
        // private mode or full: the demo still works, it just does not survive a reload
      }
    },
  };
}

const iso = (millis) => (millis == null ? null : new Date(millis).toISOString());

/** JSON with sorted keys: close enough to JCS for the values a revision holds. */
function canonical(value) {
  if (Array.isArray(value)) {
    return `[${value.map(canonical).join(',')}]`;
  }
  if (value !== null && typeof value === 'object') {
    const keys = Object.keys(value).sort();
    return `{${keys.map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`).join(',')}}`;
  }
  return JSON.stringify(value);
}

async function sha256(text) {
  const bytes = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
  return `sha256:${[...new Uint8Array(bytes)].map((byte) => byte.toString(16).padStart(2, '0')).join('')}`;
}

/**
 * @param options.clock epoch milliseconds now
 * @param options.store `{ read(), write(data) }`; sessionStorage by default
 * @param options.latencyMs delay of every answer, so that loading states show
 * @param options.stepMs delay between two phases reported by a simulated clawlet
 */
export function createDemoApi({ clock = () => Date.now(), store = sessionStore(), latencyMs = 150, stepMs = 900 } = {}) {
  let data = store.read() ?? initialData(clock());
  // a demo saved before the enrollment requests existed
  data.enrollmentRequests ??= [];
  let failNext = null;
  // instance name → token of the simulation running for it; a new one cancels the old one
  const simulations = new Map();
  const timers = new Set();

  const save = () => store.write(data);

  const later = (delay, action) => {
    const timer = setTimeout(() => {
      timers.delete(timer);
      action();
    }, delay);
    timers.add(timer);
  };

  const respond = (produce) =>
    new Promise((resolve, reject) => {
      const run = () => {
        try {
          resolve(structuredClone(produce()));
        } catch (error) {
          reject(error);
        }
      };
      if (latencyMs > 0) {
        later(latencyMs, run);
      } else {
        run();
      }
    });

  const notFound = (what) => new ApiError(404, 'ERR_NOT_FOUND', `${what} not found`);

  const findServer = (id) => {
    const server = data.servers.find((candidate) => candidate.id === id);
    if (!server) {
      throw notFound(`Server ${id}`);
    }
    return server;
  };

  /** What the current viewer sees: everything for the operator, their customer's instances otherwise. */
  const visible = (instance) => data.viewAs === null || instance.customer === data.viewAs;

  const asCustomer = () => data.viewAs !== null;

  /** The user a change is recorded under: the operator, or the first user of the customer shown. */
  const author = () => (asCustomer() ? (data.customers.find((customer) => customer.name === data.viewAs)?.users[0] ?? 'customer') : 'demo');

  const forOperators = (what) => {
    if (data.viewAs !== null) {
      throw new ApiError(403, 'ERR_FORBIDDEN', `${what} is for operators`);
    }
  };

  const findCustomer = (id) => {
    const customer = data.customers.find((candidate) => candidate.id === id);
    if (!customer) {
      throw notFound(`Customer ${id}`);
    }
    return customer;
  };

  /** The owners of a customer: its first user, until somebody changes the roles. */
  const ownersOf = (customer) => customer.owners ?? customer.users.slice(0, 1);

  const roleOf = (customer, username) => (ownersOf(customer).includes(username) ? 'owner' : 'member');

  const memberViews = (customer) =>
    [...customer.users].sort().map((username) => ({ username, role: roleOf(customer, username), since: null }));

  const customerView = (customer) => ({
    id: customer.id,
    name: customer.name,
    instances: data.instances.filter((instance) => instance.customer === customer.name).map((instance) => instance.name),
    members: memberViews(customer),
  });

  /** A customer the viewer may see: any for the operator, their own for a customer user (404 otherwise). */
  const seenCustomer = (id) => {
    const customer = findCustomer(id);
    if (asCustomer() && customer.name !== data.viewAs) {
      throw notFound(`Customer ${id}`);
    }
    return customer;
  };

  /** A customer the viewer may manage: any for the operator, their own if they own it (403 for a member). */
  const managedCustomer = (id) => {
    const customer = seenCustomer(id);
    if (asCustomer() && roleOf(customer, author()) !== 'owner') {
      throw new ApiError(403, 'ERR_FORBIDDEN', 'Only owners manage the customer');
    }
    return customer;
  };

  const setRole = (customer, username, role) => {
    if (role !== 'owner' && role !== 'member') {
      throw new ApiError(400, 'ERR_INVALID', `unknown role ${role}: owner or member`);
    }
    const owners = ownersOf(customer).filter((owner) => owner !== username);
    if (asCustomer() && role === 'member' && owners.length === 0 && roleOf(customer, username) === 'owner') {
      throw new ApiError(409, 'ERR_CONFLICT', 'The organization must keep at least one owner');
    }
    customer.owners = role === 'owner' ? [...owners, username] : owners;
  };

  /**
   * The id of an instance (backend: its uuid), what customers name it by: fixed by its name, so that
   * the demo data saved before ids keeps its links.
   */
  const idOf = (name) => {
    let hash = 0x811c9dc5;
    for (const char of name) {
      hash = Math.imul(hash ^ char.charCodeAt(0), 0x01000193) >>> 0;
    }
    return `ws-${hash.toString(16).padStart(8, '0')}`;
  };

  /** The name of the instance a request names, by name or by id (backend: InstanceLookup.byName). */
  const nameOf = (ref) => data.instances.find((candidate) => idOf(candidate.name) === ref)?.name ?? ref;

  /** The name of a workspace as typed (backend: InstanceCommands.checkedDisplayName). */
  const checkedDisplayName = (displayName) => {
    const trimmed = (displayName ?? '').trim();
    if (trimmed.length === 0 || trimmed.length > 60 || /\p{Cc}/u.test(trimmed)) {
      throw new ApiError(400, 'ERR_INVALID', 'a workspace needs a name of 1 to 60 characters');
    }
    return trimmed;
  };

  /** The name of the workspace (backend: PlaneQueries.displayName). */
  const displayNameOf = (instance) => instance.displayName ?? instance.name.replace(new RegExp(`^${GATEWAY_PREFIX}`), '');

  const findInstance = (name) => {
    const instance = data.instances.find((candidate) => candidate.name === name && visible(candidate));
    if (!instance) {
      throw notFound(`Workspace ${name}`);
    }
    return instance;
  };

  const findRevision = (name, revisionId) => {
    const revision = data.revisions.find((candidate) => candidate.instance === name && candidate.id === revisionId);
    if (!revision) {
      throw notFound(`Revision ${revisionId}`);
    }
    return revision;
  };

  const findInstanceUnchecked = (name) => data.instances.find((candidate) => candidate.name === name);

  const revisionRef = (revisionId) => {
    const revision = data.revisions.find((candidate) => candidate.id === revisionId);
    return revision ? { id: revision.id, number: revision.number } : null;
  };

  const isOnline = (server) => server.connectedSince != null && server.disconnectedAt == null;

  /** Closes the connection of a server, dated past the grace period so that it shows as offline at once. */
  const disconnect = (server) => {
    if (isOnline(server)) {
      server.disconnectedAt = clock() - 61_000;
    }
    for (const instance of data.instances.filter((candidate) => candidate.serverId === server.id)) {
      simulations.delete(instance.name);
    }
  };

  const serverDetail = (server) => ({
    ...serverView(server),
    instances: data.instances.filter((instance) => instance.serverId === server.id).map(instanceView),
  });

  // --- views: what the API answers

  const serverView = (server) => ({
    id: server.id,
    name: server.name,
    status: server.status,
    connection: server.connectedSince == null ? 'never' : isOnline(server) ? 'online' : 'offline',
    connectedSince: iso(server.connectedSince),
    disconnectedAt: iso(server.disconnectedAt),
    helloAt: iso(server.helloAt),
    clawletVersion: server.clawletVersion,
    imageVersions: server.imageVersions,
    // the demo clawlets do not report the state of the channels
    capabilities: server.capabilities ?? [],
    desiredGeneration: server.desiredGeneration,
    ackedGeneration: server.ackedGeneration,
    instanceCount: data.instances.filter((instance) => instance.serverId === server.id).length,
    certificateExpiresAt: iso(server.certificateExpiresAt),
    enrollmentTokenExpiresAt: server.enrollmentTokenExpiresAt > Date.now() ? iso(server.enrollmentTokenExpiresAt) : null,
    lastError: server.lastError ?? null,
    lastErrorAt: iso(server.lastErrorAt),
    lastAddress: server.lastAddress ?? null,
    expectedNetworks: server.expectedNetworks ?? [],
    clawletGeneration: server.clawletGeneration ?? null,
    aheadGeneration: server.aheadGeneration ?? null,
    holdReason: server.aheadGeneration != null ? server.holdReason ?? 'ahead' : null,
    unknownInstances: server.unknownInstances ?? [],
  });

  /** A pending enrollment request as the operator sees it (backend: EnrollmentRequests.pending). */
  const enrollmentRequestView = (request) => {
    const server = data.servers.find((candidate) => candidate.id === request.requestedServerId) ?? null;
    return {
      id: request.id,
      fingerprint: request.fingerprint,
      requestedServerId: request.requestedServerId,
      hostname: request.hostname,
      sourceAddress: request.sourceAddress,
      network: inNetworks(server?.expectedNetworks ?? [], request.sourceAddress) ? 'expected' : 'unknown',
      server: server && { id: server.id, name: server.name, status: server.status },
      serverLastAddress: server?.lastAddress ?? null,
      createdAt: iso(request.createdAt),
      lastSeenAt: iso(request.lastSeenAt),
    };
  };

  const findPendingRequest = (id) => {
    const request = data.enrollmentRequests.find((candidate) => candidate.id === id);
    if (!request) {
      throw notFound(`Enrollment request ${id}`);
    }
    if (request.status !== 'pending') {
      throw new ApiError(409, 'ERR_CONFLICT', `enrollment request ${id} is already ${request.status}`);
    }
    return request;
  };

  /** A new server, with no identity yet (backend: ServerCommands.addServer). */
  const addServer = (id, name) => {
    const trimmed = (name ?? '').trim();
    if (!/^[A-Za-z0-9_-]{1,64}$/.test(id ?? '')) {
      throw new ApiError(400, 'ERR_INVALID', "the server id must be 1 to 64 letters, digits, '-' or '_'");
    }
    if (!trimmed || trimmed.length > 200) {
      throw new ApiError(400, 'ERR_INVALID', 'the server name must be 1 to 200 characters');
    }
    if (data.servers.some((server) => server.id === id)) {
      throw new ApiError(409, 'ERR_CONFLICT', `server ${id} already exists`);
    }
    const server = {
      id,
      name: trimmed,
      status: 'active',
      connectedSince: null,
      disconnectedAt: null,
      helloAt: null,
      clawletVersion: null,
      imageVersions: [],
      desiredGeneration: 1,
      ackedGeneration: 0,
      certificateExpiresAt: null,
      enrollmentTokenExpiresAt: null,
      lastAddress: null,
      expectedNetworks: [],
    };
    data.servers.push(server);
    return server;
  };

  /** A new enrollment token: random, shown once, valid for an hour; the previous one stops working. */
  const issueToken = (server) => {
    const bytes = new Uint8Array(32);
    crypto.getRandomValues(bytes);
    const token = btoa(String.fromCharCode(...bytes)).replaceAll('+', '-').replaceAll('/', '_').replaceAll('=', '');
    server.enrollmentTokenExpiresAt = Date.now() + 60 * 60 * 1000;
    return { serverId: server.id, token, expiresAt: iso(server.enrollmentTokenExpiresAt) };
  };

  const instanceView = (instance) => {
    const server = findServer(instance.serverId);
    const { state, error } = displayedState(
      {
        serverConnectedSince: server.connectedSince,
        serverDisconnectedAt: server.disconnectedAt,
        desiredRevisionId: instance.desiredRevisionId,
        appliedRevisionId: instance.appliedRevisionId,
        desiredAckedAt: instance.desiredAckedAt,
        observedState: instance.observedState,
        observedHealth: instance.observedHealth,
        observedError: instance.observedError,
        observedAt: instance.observedAt,
      },
      clock(),
    );
    return {
      name: instance.name,
      customer: instance.customer,
      serverId: instance.serverId,
      imageVersion: instance.imageVersion,
      state,
      error,
      health: instance.observedHealth,
      observedState: instance.observedState,
      since: iso(instance.observedSince),
      desiredRevision: revisionRef(instance.desiredRevisionId),
      appliedRevision: revisionRef(instance.appliedRevisionId),
      desiredAckedAt: iso(instance.desiredAckedAt),
      restartNonce: instance.restartNonce,
      // the demo names are unique: the ref is the name
      ref: instance.name,
      canManage: manages(instance),
      id: idOf(instance.name),
      displayName: displayNameOf(instance),
      // backend: RevisionHead.assistantNames
      assistantNames: (() => {
        const desired = data.revisions.find((revision) => revision.id === instance.desiredRevisionId);
        const ids = assistantIds(desired?.params);
        return desired ? ids.map((id) => desired.params.assistants[id].agentName ?? id) : null;
      })(),
    };
  };

  /**
   * Whether the viewer changes the configuration of an instance (backend: Access.manages): the
   * operator, or an owner of its customer; a member only looks and restarts.
   */
  const manages = (instance) => {
    if (!asCustomer()) {
      return true;
    }
    const customer = data.customers.find((candidate) => candidate.name === instance.customer);
    return customer !== undefined && roleOf(customer, author()) === 'owner';
  };

  const requireManager = (instance, what) => {
    if (!manages(instance)) {
      throw new ApiError(403, 'ERR_FORBIDDEN', `${what} is for the owners of ${instance.customer}`);
    }
  };

  const findTemplate = (id) => {
    const template = data.templates.find((candidate) => candidate.id === id);
    if (!template) {
      throw notFound(`template ${id}`);
    }
    return template;
  };

  /**
   * The assistants made from a template, of every workspace (backend: instance_assistant_templates):
   * `instance.templates` holds them by assistant id, `instance.templateId` the template the
   * workspace was created from, whose operator rules it has.
   */
  const copiesOf = (template) =>
    data.instances.flatMap((instance) =>
      Object.entries(instance.templates ?? {})
        .filter(([, copy]) => copy.templateId === template.id)
        .map(([assistant, copy]) => ({ instance, assistant, templateVersion: copy.templateVersion })),
    );

  const templateSummary = (template) => ({
    id: template.id,
    name: template.name,
    description: template.description,
    imageVersion: template.imageVersion,
    assistantId: template.assistantId,
    version: template.version,
    archived: template.archivedAt != null,
    builtIn: Boolean(template.builtIn),
    assistants: copiesOf(template).length,
    behind: copiesOf(template).filter((copy) => copy.templateVersion < template.version).length,
    updatedBy: template.updatedBy,
    updatedAt: iso(template.updatedAt),
  });

  const templateView = (template) => ({
    ...templateSummary(template),
    params: template.params,
    persona: template.persona,
    operatorRules: template.operatorRules ?? '',
    copies: copiesOf(template)
      .sort((a, b) => a.instance.name.localeCompare(b.instance.name) || a.assistant.localeCompare(b.assistant))
      .map(({ instance, assistant, templateVersion }) => ({ instance: instance.name, assistant, customer: instance.customer, templateVersion })),
  });

  /** A template as an operator writes it (backend: TemplateService.write): one assistant, checked as a revision is. */
  const writeTemplate = (template, { name, description, imageVersion, assistantId = null, params, persona, operatorRules } = {}) => {
    const trimmed = (name ?? '').trim();
    if (trimmed.length === 0 || trimmed.length > 100) {
      throw new ApiError(400, 'ERR_INVALID', 'a template needs a name of 1 to 100 characters');
    }
    if (data.templates.some((other) => other.name === trimmed && other.id !== template.id)) {
      throw new ApiError(409, 'ERR_CONFLICT', `template ${trimmed} already exists`);
    }
    if (!IMAGE_VERSIONS[imageVersion]) {
      throw new ApiError(400, 'ERR_INVALID', `image version ${imageVersion} has no params schema`);
    }
    const id = checkedAssistantId(assistantId ?? template.assistantId ?? assistantIdOf(trimmed));
    if (params?.assistants) {
      throw new ApiError(400, 'ERR_INVALID', 'a template is one assistant: its params go without assistants around them');
    }
    if (persona?.soul == null || persona?.agents == null || persona?.user == null) {
      throw new ApiError(400, 'ERR_INVALID', 'a template needs a persona: soul, agents and user');
    }
    const errors = validate(assistantSchema(IMAGE_VERSIONS[imageVersion].paramsSchema), params ?? {});
    if (Object.keys(errors).length > 0) {
      const [field, message] = Object.entries(errors)[0];
      throw new ApiError(400, 'ERR_INVALID', `${field}: ${message}`);
    }
    Object.assign(template, {
      name: trimmed,
      description: (description ?? '').trim(),
      imageVersion,
      assistantId: id,
      params: structuredClone(params),
      persona: { soul: persona.soul, agents: persona.agents, user: persona.user },
      operatorRules: operatorRules ?? '',
      updatedBy: author(),
      updatedAt: clock(),
    });
  };

  // what a customer user does not get (backend: the forCustomer of each view)
  const customerInstance = (view) => ({ ...view, name: null, ref: view.id, serverId: null, error: null, observedState: null, desiredAckedAt: null, restartNonce: null });
  /** The template each assistant of a revision starts from, by assistant id. */
  const linkedTemplates = (revision) => Object.fromEntries(Object.entries(revision.templates ?? {}).map(([id, link]) => [id, link.templateId]));
  const customerRevision = (view) => ({ ...view, digest: null, templates: {} });
  const shownInstance = (instance) => (asCustomer() ? customerInstance(instanceView(instance)) : instanceView(instance));
  const shownRevision = (view) => (asCustomer() ? customerRevision(view) : view);

  /**
   * The paths of the operator's values that differ between two params (backend:
   * InstanceCommands.changedOperatorValues): an assistant added or taken away is one of them.
   */
  const changedOperatorValues = (instance, params, other) => {
    const schema = IMAGE_VERSIONS[instance.imageVersion]?.paramsSchema ?? {};
    const after = operatorValues(schema, params);
    const before = operatorValues(schema, other);
    return [...new Set([...Object.keys(after), ...Object.keys(before)])]
      .sort()
      .filter((path) => canonical(after[path] ?? null) !== canonical(before[path] ?? null));
  };

  /** A customer user keeps what the operator chose (backend: InstanceCommands.requireOperatorParamsKept). */
  const requireOperatorParamsKept = (instance, params) => {
    if (!asCustomer()) {
      return;
    }
    const desired = data.revisions.find((revision) => revision.id === instance.desiredRevisionId)?.params ?? {};
    const changed = changedOperatorValues(instance, params, desired);
    if (changed.length > 0) {
      throw new ApiError(403, 'ERR_FORBIDDEN', `only an operator may change ${changed.join(', ')}`);
    }
  };

  const desiredRules = (instance) => data.revisions.find((revision) => revision.id === instance.desiredRevisionId)?.operatorRules ?? '';

  /** A customer user keeps the operator rules as they keep the model (backend: requireOperatorRulesKept). */
  const requireOperatorRulesKept = (instance, rules) => {
    if (asCustomer() && (rules ?? '') !== desiredRules(instance)) {
      throw new ApiError(403, 'ERR_FORBIDDEN', 'only an operator may change the operator rules');
    }
  };

  const revisionSummary = (revision) => ({
    id: revision.id,
    number: revision.number,
    status: revision.status,
    digest: revision.digest,
    author: revision.author,
    createdAt: iso(revision.createdAt),
  });

  const eventView = (event) => ({ ...event, at: iso(event.at) });

  /**
   * The secrets of the workspace, then those of each of its assistants, named <NAME>__<ID>: the
   * assistants of its desired revision, or of its newest one before it has one, but the
   * specialists of a team, which have no bot (backend: SecretService.declaredSecrets).
   */
  const declaredSecrets = (instance) => {
    const schema = IMAGE_VERSIONS[instance.imageVersion]?.paramsSchema ?? {};
    const own = schema['x-secrets'] ?? [];
    const each = schema['x-assistant-secrets'] ?? [];
    const revisions = data.revisions.filter((revision) => revision.instance === instance.name);
    const revision = revisions.find((candidate) => candidate.id === instance.desiredRevisionId) ?? revisions.sort((a, b) => b.number - a.number)[0];
    return [
      ...own,
      ...assistantIds(revision?.params).filter((id) => !specialistsOf(revision.params).has(id)).flatMap((id) =>
        each.map((secret) => ({ ...secret, name: assistantSecretName(secret.name, id), title: `${secret.title} (${id})` })),
      ),
    ];
  };

  /**
   * Backend: InstanceCommands.followTemplates. An assistant whose operator's part changed by hand is
   * behind its template, and so are those of the template of origin when the rules changed; one the
   * revision drops is no longer its template's.
   */
  const followTemplates = (instance, revision) => {
    const desired = data.revisions.find((candidate) => candidate.id === instance.desiredRevisionId);
    if (!instance.templates || !desired || desired.id === revision.id) {
      return;
    }
    const changed = changedOperatorValues(instance, revision.params, desired.params);
    const rulesChanged = (revision.operatorRules ?? '') !== (desired.operatorRules ?? '');
    for (const [id, copy] of Object.entries(instance.templates)) {
      if (!(id in revision.params.assistants)) {
        delete instance.templates[id];
      } else if (changed.some((path) => path.startsWith(`assistants.${id}.`)) || (rulesChanged && copy.templateId === instance.templateId)) {
        copy.templateVersion = 0;
      }
    }
  };

  /**
   * Backend: InstanceCommands.followRevisionTemplates. The assistants a revision starts from a
   * template came from it once the revision is applied, unless they already came from one; a first
   * revision with the rules of their template makes it the template of origin.
   */
  const followRevisionTemplates = (instance, revision) => {
    for (const [id, link] of Object.entries(revision.templates ?? {})) {
      if (!(id in revision.params.assistants) || instance.templates?.[id]) {
        continue;
      }
      instance.templates = { ...instance.templates, [id]: { ...link } };
      if (!instance.desiredRevisionId && !instance.templateId && (findTemplate(link.templateId).operatorRules ?? '') === (revision.operatorRules ?? '')) {
        instance.templateId = link.templateId;
      }
    }
  };

  /**
   * The template of an assistant of a new revision (backend: InstanceCommands.linkTemplate): its
   * version, or 0 when the operator's part of the assistant is not the template's.
   */
  const templateLink = (instance, params, id, templateId) => {
    const assistant = params.assistants?.[id];
    if (!assistant) {
      throw new ApiError(400, 'ERR_INVALID', `the revision has no assistant ${id} to start from a template`);
    }
    const template = findTemplate(templateId);
    if (template.archivedAt != null) {
      throw new ApiError(409, 'ERR_CONFLICT', `template ${template.name} is archived: put it back in use first`);
    }
    if (template.imageVersion !== instance.imageVersion) {
      throw new ApiError(400, 'ERR_INVALID', `template ${template.name} is for image version ${template.imageVersion}, the workspace runs ${instance.imageVersion}`);
    }
    const same = operatorParams(assistantSchema(IMAGE_VERSIONS[instance.imageVersion].paramsSchema) ?? {}).every(
      (name) => JSON.stringify(assistant[name]) === JSON.stringify(template.params[name]),
    );
    return { templateId: template.id, templateVersion: same ? template.version : 0 };
  };

  /**
   * The pairing requests of a workspace nobody ignored, of the assistants of its desired revision,
   * without the senders they already allow (backend: PlaneQueries.pairingRequestsOf).
   */
  const waitingRequests = (instance) => {
    const assistants = data.revisions.find((revision) => revision.id === instance.desiredRevisionId)?.params.assistants ?? {};
    return (data.pairingRequests ?? [])
      .filter((request) => request.instance === instance.name && !request.ignored && request.assistant in assistants)
      .filter((request) => !(assistants[request.assistant][channelOf(request)]?.allowFrom ?? []).includes(request.senderId))
      .map(({ assistant, channel = 'telegram', senderId, code, name, username, requestedAt }) => ({ assistant, channel, senderId, code, name, username, requestedAt: iso(requestedAt) }));
  };

  /** The channel of a pairing request of the fixtures: Telegram when it does not name one. */
  const channelOf = (request) => request.channel ?? 'telegram';

  const findRequest = (instance, assistant, channel, senderId) => {
    const request = (data.pairingRequests ?? []).find(
      (candidate) =>
        candidate.instance === instance.name && candidate.assistant === assistant && channelOf(candidate) === channel && candidate.senderId === senderId && !candidate.ignored,
    );
    if (!request) {
      throw new ApiError(404, 'ERR_NOT_FOUND', `no request from ${senderId} on ${channel} to write to assistant ${assistant}: it expired, or it was allowed or ignored`);
    }
    return request;
  };

  const findSecret = (instance, name) => data.secrets.find((secret) => secret.instance === instance && secret.name === name);

  const secretView = (declared, secret) => ({
    name: declared.name,
    title: declared.title ?? declared.name,
    description: declared.description ?? null,
    set: Boolean(secret),
    version: secret?.version ?? null,
    updatedAt: iso(secret?.setAt),
    setBy: secret?.setBy ?? null,
    deliveredAt: iso(secret?.deliveredAt),
    rejection: secret?.rejection ?? null,
  });

  // newest first; reversed before the (stable) sort so that two events of the same millisecond
  // keep the order they were reported in
  const eventsOf = (name, limit) =>
    data.events
      .filter((event) => (name ? event.instance === name : visible(findInstanceUnchecked(event.instance))))
      .reverse()
      .sort((a, b) => b.at - a.at)
      .slice(0, limit)
      .map(eventView);

  /** AGENTS.md as the clawlet receives it (backend: ConfigurationContent.composed): the operator rules, then the rest. */
  const composedPersona = (persona, rules) =>
    rules
      ? {
          ...persona,
          agents: `<!-- Rules of the provider: the customer cannot change them -->\n${rules.endsWith('\n') ? rules : `${rules}\n`}\n<!-- Rules of the customer -->\n${persona.agents}`,
        }
      : persona;

  const ensureDigest = async (revision) => {
    const content = {
      params: revision.params,
      personas: Object.fromEntries(Object.entries(revision.personas).map(([id, persona]) => [id, composedPersona(persona, revision.operatorRules)])),
    };
    revision.digest ??= await sha256(canonical(content));
    return revision;
  };

  /** Refuses personas that do not name the assistants of the params (backend: ConfigurationContent.checkWritten). */
  const checkPersonas = (params, personas) => {
    const complete = (candidate) => candidate?.soul != null && candidate?.agents != null && candidate?.user != null;
    if (personas == null || Object.keys(personas).length === 0) {
      throw new ApiError(400, 'ERR_INVALID', 'a revision has personas, one for each assistant');
    }
    for (const [id, each] of Object.entries(personas)) {
      if (!complete(each)) {
        throw new ApiError(400, 'ERR_INVALID', `personas.${id} must have soul, agents and user`);
      }
    }
    const ids = assistantIds(params);
    if (canonical(ids) !== canonical(Object.keys(personas).sort())) {
      throw new ApiError(400, 'ERR_INVALID', `personas must name the same assistants as params.assistants: [${ids.join(', ')}]`);
    }
  };

  // --- the simulated clawlet

  const record = (instance, revision, phase, status, message = null) => {
    data.sequence += 1;
    data.events.push({
      id: `evt_demo_${data.sequence}`,
      instance: instance.name,
      revisionId: revision?.id ?? null,
      revisionNumber: revision?.number ?? null,
      phase,
      status,
      message,
      at: clock(),
    });
  };

  /**
   * Runs the phases of an apply of the desired revision, one every `stepMs`, as the clawlet would
   * report them. `steps` is a list of functions; each one runs only while the simulation it
   * belongs to is still the current one for the instance.
   */
  const simulate = (instance, steps) => {
    const token = Symbol(instance.name);
    simulations.set(instance.name, token);
    steps.forEach((step, index) => {
      later((index + 1) * stepMs, () => {
        if (simulations.get(instance.name) !== token) {
          return;
        }
        const current = data.instances.find((candidate) => candidate.name === instance.name);
        if (!current || !isOnline(findServer(current.serverId))) {
          simulations.delete(instance.name);
          return;
        }
        step(current);
        // the clawlet reports its observed state after every phase
        current.observedAt = clock();
        if (index === steps.length - 1) {
          simulations.delete(instance.name);
        }
        save();
      });
    });
  };

  const acknowledge = (instance) => {
    const server = findServer(instance.serverId);
    server.ackedGeneration = server.desiredGeneration;
    instance.desiredAckedAt = clock();
    instance.observedState = 'applying';
    instance.observedSince = clock();
    instance.observedError = null;
  };

  const startApply = (instance, failure) => {
    const target = () => data.revisions.find((revision) => revision.id === instance.desiredRevisionId);
    const previous = instance.appliedRevisionId;
    const phase = (name, status = 'ok', message = null) => (current) => record(current, target(), name, status, message);
    const steps = [acknowledge, phase('render'), phase('validate')];

    if (failure === 'validate') {
      const message = 'params: thinking: "high" is not supported by deepseek/deepseek-v4-flash';
      steps.pop();
      steps.push(phase('validate', 'failed', message), (current) => {
        current.observedState = 'failed';
        current.observedError = message;
        current.observedSince = clock();
      });
      simulate(instance, steps);
      return;
    }

    steps.push(phase('secrets'), phase('write'), phase('reload'));
    if (failure === 'health' || failure === 'rollback') {
      const message = '/startupz: no 200 response after 240 s';
      const previousNumber = revisionRef(previous)?.number;
      steps.push(phase('health', 'failed', message), phase('rollback', 'started', `back to revision ${previousNumber}`));
      if (failure === 'health') {
        steps.push(phase('rollback', 'ok', `revision ${previousNumber} running`), (current) => {
          current.appliedRevisionId = previous;
          current.observedState = 'rolled_back';
          current.observedError = message;
          current.observedSince = clock();
        });
      } else {
        const rollbackMessage = `revision ${previousNumber} did not start either: /startupz: no 200 response after 240 s`;
        steps.push(phase('rollback', 'failed', rollbackMessage), (current) => {
          current.appliedRevisionId = null;
          current.observedState = 'rollback_failed';
          current.observedError = rollbackMessage;
          current.observedSince = clock();
        });
      }
      simulate(instance, steps);
      return;
    }

    steps.push(phase('health'), (current) => {
      record(current, target(), 'done', 'ok', current.brokenSecret ? `readyz: ${current.brokenSecret} rejected by the channel (401)` : null);
      current.appliedRevisionId = current.desiredRevisionId;
      current.observedState = 'running';
      current.observedHealth = current.brokenSecret ? 'degraded' : 'ready';
      current.observedSince = clock();
    });
    simulate(instance, steps);
  };

  /**
   * The clawlet stops the instance and removes it, keeping its state volume; then it no longer
   * reports it, and the plane forgets what it observed (LinkReports.observed).
   */
  const startRemove = (instance) => {
    simulate(instance, [
      acknowledge,
      (current) => {
        current.appliedRevisionId = null;
        current.observedState = null;
        current.observedHealth = null;
        current.observedSince = null;
        current.observedError = null;
      },
    ]);
  };

  const startRestart = (instance) => {
    const applied = () => data.revisions.find((revision) => revision.id === instance.appliedRevisionId);
    simulate(instance, [
      (current) => {
        const server = findServer(current.serverId);
        server.ackedGeneration = server.desiredGeneration;
        current.desiredAckedAt = clock();
        current.observedHealth = 'starting';
      },
      (current) => record(current, applied(), 'reload', 'ok', `restart ${current.restartNonce}`),
      (current) => record(current, applied(), 'health', 'ok'),
      (current) => {
        record(current, applied(), 'done', 'ok', current.brokenSecret ? `readyz: ${current.brokenSecret} rejected by the channel (401)` : null);
        current.observedHealth = current.brokenSecret ? 'degraded' : 'ready';
        current.observedSince = clock();
      },
    ]);
  };

  /** The clawlet receives a secret, reports holding it, and restarts the instance with it. */
  const startSecretApply = (instance, secret) => {
    const applied = () => data.revisions.find((revision) => revision.id === instance.appliedRevisionId);
    const version = secret.version;
    simulate(instance, [
      (current) => {
        const held = findSecret(current.name, secret.name);
        if (held && held.version === version) {
          held.deliveredAt = clock();
        }
        if (current.brokenSecret === secret.name) {
          current.brokenSecret = null;
        }
        const server = findServer(current.serverId);
        server.ackedGeneration = server.desiredGeneration;
        current.desiredAckedAt = clock();
        current.observedHealth = 'starting';
      },
      (current) => record(current, applied(), 'secrets', 'ok', `${secret.name} version ${version}`),
      (current) => record(current, applied(), 'reload', 'ok'),
      (current) => record(current, applied(), 'health', 'ok'),
      (current) => {
        record(current, applied(), 'done', 'ok', current.brokenSecret ? `readyz: ${current.brokenSecret} rejected by the channel (401)` : null);
        current.observedHealth = current.brokenSecret ? 'degraded' : 'ready';
        current.observedSince = clock();
      },
    ]);
  };

  const changeDesired = (instance) => {
    const server = findServer(instance.serverId);
    server.desiredGeneration += 1;
    instance.desiredAckedAt = null;
    instance.stuckSimulated = false;
  };

  /** What a clawlet does after its hello: reconcile every instance whose desired state differs. */
  const reconcile = (server) => {
    if (server.aheadGeneration != null) {
      // held: nothing is sent to its clawlet (backend: ClawletSession.sendDesired)
      return;
    }
    data.instances
      .filter((instance) => instance.serverId === server.id)
      .filter((instance) => !instance.stuckSimulated && !simulations.has(instance.name))
      .filter((instance) => instance.desiredRevisionId !== instance.appliedRevisionId && !['failed', 'rolled_back', 'rollback_failed'].includes(instance.observedState))
      .forEach((instance) => (instance.desiredRevisionId == null ? startRemove(instance) : startApply(instance, null)));
  };

  // An apply in progress when the page was reloaded lost its timers: start it again.
  data.servers.filter(isOnline).forEach(reconcile);

  const api = {
    session: () =>
      respond(() => {
        if (data.viewAs === null) {
          return { username: 'demo', admin: true, operator: true, customers: [], memberships: [] };
        }
        const customer = data.customers.find((candidate) => candidate.name === data.viewAs);
        const username = customer?.users[0] ?? 'customer';
        const memberships = customer ? [{ id: customer.id, name: customer.name, role: roleOf(customer, username) }] : [];
        return { username, admin: false, operator: false, customers: [data.viewAs], memberships };
      }),

    servers: () =>
      respond(() => {
        forOperators('The list of servers');
        return data.servers.map(serverView);
      }),

    server: (id) =>
      respond(() => {
        forOperators('A server');
        return serverDetail(findServer(id));
      }),

    instances: () => respond(() => data.instances.filter(visible).map(shownInstance)),

    instance: async (name) => {
      await Promise.all(data.revisions.filter((revision) => revision.instance === name).map(ensureDigest));
      return respond(() => {
        const instance = findInstance(name);
        return {
          ...shownInstance(instance),
          revisions: data.revisions
            .filter((revision) => revision.instance === name)
            .sort((a, b) => b.number - a.number)
            .map((revision) => shownRevision(revisionSummary(revision))),
          secrets: declaredSecrets(instance).map((declared) => secretView(declared, findSecret(name, declared.name))),
          // the apply events are the operator's (backend: PlaneQueries.instance)
          events: asCustomer() ? [] : eventsOf(name, 40),
          pairingRequests: manages(instance) ? waitingRequests(instance) : [],
          // the demo servers do not report the state of the channels (backend: PlaneQueries.channelsOf)
          channels: null,
        };
      });
    },

    revision: async (name, revisionId) => {
      const revision = await ensureDigest(findRevision(name, revisionId));
      return respond(() =>
        shownRevision({ ...revisionSummary(revision), params: revision.params, personas: revision.personas, operatorRules: revision.operatorRules ?? '', templates: linkedTemplates(revision) }),
      );
    },

    /**
     * `operatorRules` null or left out keeps those of the desired revision; only an operator changes
     * them, and only an operator starts assistants from a template (`templates`, by assistant id).
     */
    saveRevision: async (name, { params, personas = null, operatorRules = null, templates = null }) => {
      const instance = findInstance(name);
      requireManager(instance, 'changing a workspace');
      checkPersonas(params, personas);
      const errors = { ...validate(IMAGE_VERSIONS[instance.imageVersion].paramsSchema, params), ...teamErrors(params) };
      if (Object.keys(errors).length > 0) {
        const [field, message] = Object.entries(errors)[0];
        throw new ApiError(422, 'ERR_INVALID_PARAMS', `${field}: ${message}`);
      }
      requireOperatorParamsKept(instance, params);
      const rules = operatorRules ?? desiredRules(instance);
      requireOperatorRulesKept(instance, rules);
      if (Object.keys(templates ?? {}).length > 0) {
        forOperators('Starting an assistant from a template');
      }
      const links = Object.fromEntries(Object.entries(templates ?? {}).map(([id, templateId]) => [id, templateLink(instance, params, id, templateId)]));
      const number = Math.max(0, ...data.revisions.filter((revision) => revision.instance === name).map((revision) => revision.number)) + 1;
      data.sequence += 1;
      const revision = {
        id: `rev_demo_${data.sequence}`,
        instance: name,
        number,
        status: 'draft',
        author: author(),
        createdAt: clock(),
        digest: null,
        params,
        personas,
        operatorRules: rules,
        templates: links,
      };
      await ensureDigest(revision);
      data.revisions.push(revision);
      save();
      return respond(() => shownRevision({ ...revisionSummary(revision), params, personas, operatorRules: rules, templates: linkedTemplates(revision) }));
    },

    /** Backend: InstanceCommands.addAssistantFromTemplate. One more assistant, applied at once; the rules stay. */
    addAssistantFromTemplate: async (name, { templateId, assistantId = null } = {}) => {
      forOperators('Adding an assistant');
      const instance = findInstance(name);
      const template = findTemplate(templateId);
      if (template.archivedAt != null) {
        throw new ApiError(409, 'ERR_CONFLICT', `template ${template.name} is archived: put it back in use first`);
      }
      if (template.imageVersion !== instance.imageVersion) {
        throw new ApiError(400, 'ERR_INVALID', `template ${template.name} is for image version ${template.imageVersion}, the workspace runs ${instance.imageVersion}`);
      }
      const id = checkedAssistantId(assistantId ?? template.assistantId);
      const desired = data.revisions.find((revision) => revision.id === instance.desiredRevisionId);
      const params = structuredClone(desired?.params ?? { assistants: {} });
      if (id in params.assistants) {
        throw new ApiError(409, 'ERR_CONFLICT', `the workspace already has an assistant ${id}: choose another id`);
      }
      params.assistants[id] = structuredClone(template.params);
      const personas = { ...structuredClone(desired?.personas ?? {}), [id]: structuredClone(template.persona) };
      const errors = validate(IMAGE_VERSIONS[instance.imageVersion].paramsSchema, params);
      if (Object.keys(errors).length > 0) {
        const [field, message] = Object.entries(errors)[0];
        throw new ApiError(400, 'ERR_INVALID', `${field}: ${message}`);
      }
      const server = findServer(instance.serverId);
      if (server.imageVersions.length > 0 && !server.imageVersions.includes(instance.imageVersion)) {
        throw new ApiError(409, 'ERR_IMAGE_VERSION', `Server ${server.id} does not declare image version ${instance.imageVersion}`);
      }
      if (!desired) {
        instance.templateId = template.id;
      }
      data.sequence += 1;
      const revision = {
        id: `rev_demo_${data.sequence}`,
        instance: name,
        number: Math.max(0, ...data.revisions.filter((candidate) => candidate.instance === name).map((candidate) => candidate.number)) + 1,
        status: 'published',
        author: author(),
        createdAt: clock(),
        digest: null,
        params,
        personas,
        operatorRules: desired ? (desired.operatorRules ?? '') : (template.operatorRules ?? ''),
      };
      await ensureDigest(revision);
      data.revisions.push(revision);
      instance.templates = { ...instance.templates, [id]: { templateId: template.id, templateVersion: template.version } };
      instance.desiredRevisionId = revision.id;
      changeDesired(instance);
      simulations.delete(name);
      if (isOnline(server)) {
        startApply(instance, null);
      }
      save();
      return respond(() => instanceView(instance));
    },

    /**
     * Backend: InstanceCommands.allowPairingRequest. The sender joins the allowed users of the
     * channel, in a revision applied at once.
     */
    allowPairingRequest: async (name, assistant, channel, senderId) => {
      const instance = findInstance(name);
      requireManager(instance, 'allowing someone to write to an assistant');
      const request = findRequest(instance, assistant, channel, senderId);
      const desired = data.revisions.find((revision) => revision.id === instance.desiredRevisionId);
      if (!desired?.params.assistants[assistant]) {
        throw new ApiError(409, 'ERR_CONFLICT', `the workspace no longer has an assistant ${assistant}`);
      }
      data.pairingRequests = data.pairingRequests.filter((candidate) => candidate !== request);
      const params = structuredClone(desired.params);
      const settings = (params.assistants[assistant][channel] ??= {});
      settings.allowFrom = [...new Set([...(settings.allowFrom ?? []), senderId])];
      data.sequence += 1;
      const revision = {
        id: `rev_demo_${data.sequence}`,
        instance: name,
        number: Math.max(0, ...data.revisions.filter((candidate) => candidate.instance === name).map((candidate) => candidate.number)) + 1,
        status: 'published',
        author: author(),
        createdAt: clock(),
        digest: null,
        params,
        personas: structuredClone(desired.personas),
        operatorRules: desired.operatorRules ?? '',
      };
      await ensureDigest(revision);
      data.revisions.push(revision);
      instance.desiredRevisionId = revision.id;
      changeDesired(instance);
      simulations.delete(name);
      if (isOnline(findServer(instance.serverId))) {
        startApply(instance, null);
      }
      save();
      return respond(() => shownInstance(instance));
    },

    /** Backend: InstanceCommands.ignorePairingRequest. Hidden until it expires. */
    ignorePairingRequest: (name, assistant, channel, senderId) =>
      respond(() => {
        const instance = findInstance(name);
        requireManager(instance, 'ignoring a request to write to an assistant');
        findRequest(instance, assistant, channel, senderId).ignored = true;
        save();
        return shownInstance(instance);
      }),

    apply: (name, revisionId) =>
      respond(() => {
        const instance = findInstance(name);
        requireManager(instance, 'changing a workspace');
        const revision = findRevision(name, revisionId);
        const server = findServer(instance.serverId);
        if (revision.id !== instance.desiredRevisionId) {
          // going back to an old revision must not bring back a model, or rules, the operator has since replaced
          requireOperatorParamsKept(instance, revision.params);
          requireOperatorRulesKept(instance, revision.operatorRules);
        }
        if (!server.imageVersions.includes(instance.imageVersion)) {
          throw new ApiError(409, 'ERR_IMAGE_VERSION', `Server ${server.id} does not declare image version ${instance.imageVersion}`);
        }
        followTemplates(instance, revision);
        followRevisionTemplates(instance, revision);
        revision.status = 'published';
        instance.desiredRevisionId = revision.id;
        changeDesired(instance);
        const failure = failNext;
        failNext = null;
        simulations.delete(instance.name);
        if (isOnline(server)) {
          startApply(instance, failure);
        }
        save();
        return shownInstance(instance);
      }),

    removeInstance: (name) =>
      respond(() => {
        forOperators('Removing a workspace');
        const instance = findInstance(name);
        if (instance.desiredRevisionId == null) {
          throw new ApiError(409, 'ERR_CONFLICT', 'the workspace has no desired revision: it is not on its server');
        }
        instance.desiredRevisionId = null;
        changeDesired(instance);
        simulations.delete(instance.name);
        if (isOnline(findServer(instance.serverId))) {
          startRemove(instance);
        }
        save();
        return shownInstance(instance);
      }),

    deleteInstance: (name) =>
      respond(() => {
        forOperators('Deleting a workspace');
        const instance = findInstance(name);
        const ownedBy = (item) => item.instance === name;
        if (instance.desiredRevisionId != null || data.revisions.some((revision) => ownedBy(revision) && revision.status === 'published')) {
          throw new ApiError(409, 'ERR_CONFLICT', 'the workspace was deployed: its revisions are history, remove it from its server instead');
        }
        data.instances = data.instances.filter((candidate) => candidate !== instance);
        data.revisions = data.revisions.filter((revision) => !ownedBy(revision));
        data.secrets = data.secrets.filter((secret) => !ownedBy(secret));
        data.events = data.events.filter((event) => !ownedBy(event));
        simulations.delete(name);
        save();
        return name;
      }),

    renameWorkspace: (name, displayName) =>
      respond(() => {
        const instance = findInstance(name);
        requireManager(instance, 'renaming a workspace');
        instance.displayName = checkedDisplayName(displayName);
        save();
        return shownInstance(instance);
      }),

    restart: (name) =>
      respond(() => {
        const instance = findInstance(name);
        instance.restartNonce += 1;
        changeDesired(instance);
        if (isOnline(findServer(instance.serverId))) {
          startRestart(instance);
        }
        save();
        return shownInstance(instance);
      }),

    setSecret: (name, secretName, value) =>
      respond(() => {
        const instance = findInstance(name);
        requireManager(instance, 'setting a secret');
        const declared = declaredSecrets(instance).find((candidate) => candidate.name === secretName);
        if (!declared) {
          throw notFound(`image version ${instance.imageVersion} has no secret ${secretName}`);
        }
        if (!value) {
          throw new ApiError(400, 'ERR_INVALID', 'a secret has 1 to 8192 bytes');
        }
        const server = findServer(instance.serverId);
        if (server.connectedSince == null) {
          throw new ApiError(409, 'ERR_CONFLICT', `server ${server.id} has never connected: there is no key to seal the secret to yet`);
        }
        let secret = findSecret(name, secretName);
        if (!secret) {
          secret = { instance: name, name: secretName, version: 0 };
          data.secrets.push(secret);
        }
        Object.assign(secret, { version: secret.version + 1, setAt: clock(), setBy: author(), deliveredAt: null, rejection: null });
        // a new value is a change of desired state: the clawlet receives it, reports it, and
        // restarts the instance with it
        changeDesired(instance);
        if (isOnline(server)) {
          startSecretApply(instance, secret);
        }
        save();
        return secretView(declared, secret);
      }),

    events: ({ instance, limit = 20 } = {}) =>
      respond(() => {
        forOperators('The apply events');
        if (instance) {
          findInstance(instance);
        }
        return eventsOf(instance, limit);
      }),

    customers: () =>
      respond(() => {
        forOperators('Managing customers');
        return [...data.customers].sort((a, b) => a.name.localeCompare(b.name)).map(customerView);
      }),

    createServer: (id, name) =>
      respond(() => {
        forOperators('Adding a server');
        const server = addServer(id, name);
        const issued = issueToken(server);
        save();
        return issued;
      }),

    /**
     * From a template the instance gets the template as revision 1, applied at once: one assistant,
     * with the id the template proposes or `assistantId` (backend: ServerCommands.addInstance).
     */
    addInstance: async (serverId, { name, displayName = null, imageVersion, customerId, templateId = null, assistantId = null } = {}) => {
      const template = templateId == null ? null : data.templates.find((candidate) => candidate.id === templateId);
      if (template == null && assistantId != null) {
        throw new ApiError(400, 'ERR_INVALID', 'the id of an assistant comes with a template: a workspace without one has no assistant until its first revision');
      }
      const id = template && checkedAssistantId(assistantId ?? template.assistantId);
      const first = template && {
        id: `rev_demo_${data.sequence + 1}`,
        params: { assistants: { [id]: structuredClone(template.params) } },
        personas: { [id]: structuredClone(template.persona) },
        operatorRules: template.operatorRules ?? '',
        digest: null,
      };
      if (first) {
        await ensureDigest(first);
      }
      return respond(() => {
        forOperators('Adding a workspace');
        const server = findServer(serverId);
        // the slug, or the whole name (backend: GatewayNames.fromInput)
        const slug = (name ?? '').replace(new RegExp(`^${GATEWAY_PREFIX}`), '');
        if (!/^[a-z0-9]([a-z0-9-]{0,30}[a-z0-9])?$/.test(slug)) {
          throw new ApiError(400, 'ERR_INVALID', "the workspace id must be 1 to 32 lowercase letters, digits or '-', and neither start nor end with '-'");
        }
        name = `${GATEWAY_PREFIX}${slug}`;
        const shownName = displayName == null ? null : checkedDisplayName(displayName);
        if (templateId != null) {
          findTemplate(templateId);
          if (template.archivedAt != null) {
            throw new ApiError(409, 'ERR_CONFLICT', `template ${template.name} is archived: put it back in use first`);
          }
          if (imageVersion && imageVersion !== template.imageVersion) {
            throw new ApiError(400, 'ERR_INVALID', `template ${template.name} is for image version ${template.imageVersion}, not ${imageVersion}`);
          }
          imageVersion = template.imageVersion;
        }
        if (!IMAGE_VERSIONS[imageVersion]) {
          throw new ApiError(400, 'ERR_INVALID', `image version ${imageVersion} has no params schema`);
        }
        // the demo names are unique across servers: the ref of an instance is its name
        if (findInstanceUnchecked(name)) {
          throw new ApiError(409, 'ERR_CONFLICT', `a workspace with the id ${slug} already exists`);
        }
        const customer = customerId == null ? null : findCustomer(customerId).name;
        const instance = {
          name,
          ...(shownName == null ? {} : { displayName: shownName }),
          customer,
          serverId: server.id,
          imageVersion,
          desiredRevisionId: null,
          appliedRevisionId: null,
          desiredAckedAt: null,
          observedState: null,
          observedHealth: null,
          observedError: null,
          observedSince: null,
          observedAt: null,
          restartNonce: 0,
          stuckSimulated: false,
          brokenSecret: null,
          templateId: template?.id ?? null,
          templates: template ? { [id]: { templateId: template.id, templateVersion: template.version } } : {},
        };
        data.instances.push(instance);
        changeDesired(instance);
        if (first) {
          data.sequence += 1;
          data.revisions.push({ ...first, id: `rev_demo_${data.sequence}`, instance: name, number: 1, status: 'published', author: author(), createdAt: clock() });
          instance.desiredRevisionId = `rev_demo_${data.sequence}`;
          changeDesired(instance);
          if (isOnline(server)) {
            startApply(instance, null);
          }
        }
        save();
        return instanceView(instance);
      });
    },

    /** Operators only: the templates in use, by name; `archived` lists the archived ones too. */
    templates: ({ archived = false } = {}) =>
      respond(() => {
        forOperators('Managing templates');
        return data.templates
          .filter((template) => archived || template.archivedAt == null)
          .sort((a, b) => a.name.localeCompare(b.name))
          .map(templateSummary);
      }),

    template: (id) =>
      respond(() => {
        forOperators('Managing templates');
        return templateView(findTemplate(id));
      }),

    /** Backend: TemplateService.create. `fromInstance` gives the operator's part of that instance, nothing of its customer's. */
    createTemplate: ({ fromInstance = null, ...content }) =>
      respond(() => {
        forOperators('Managing templates');
        if (fromInstance) {
          const instance = findInstance(fromInstance);
          const desired = data.revisions.find((revision) => revision.id === instance.desiredRevisionId);
          if (!desired) {
            throw new ApiError(409, 'ERR_CONFLICT', 'the workspace has no desired revision to make a template of');
          }
          const ids = assistantIds(desired.params);
          const source = content.assistantId ?? (ids.length === 1 ? ids[0] : null);
          if (source == null || !ids.includes(source)) {
            throw new ApiError(400, 'ERR_INVALID', `the workspace has ${ids.length} assistants: the id of the template names the one to take the model and the thinking of`);
          }
          const assistant = desired.params.assistants[source];
          const params = structuredClone(content.params ?? {});
          for (const name of operatorParams(assistantSchema(IMAGE_VERSIONS[instance.imageVersion]?.paramsSchema ?? {}) ?? {})) {
            if (name in assistant) {
              params[name] = structuredClone(assistant[name]);
            } else {
              delete params[name];
            }
          }
          content = { ...content, imageVersion: instance.imageVersion, assistantId: source, params, operatorRules: desired.operatorRules ?? '' };
        }
        data.sequence += 1;
        const template = { id: `tpl_demo_${data.sequence}`, version: 1, createdBy: author(), archivedAt: null };
        writeTemplate(template, content);
        data.templates.push(template);
        save();
        return templateView(template);
      }),

    updateTemplate: (id, content) =>
      respond(() => {
        forOperators('Managing templates');
        const template = findTemplate(id);
        if (template.builtIn) {
          throw new ApiError(409, 'ERR_CONFLICT', `template ${template.name} comes with the plane, which changes it at every update: duplicate it to make one of your own`);
        }
        if (content?.version == null) {
          throw new ApiError(400, 'ERR_INVALID', 'a change of a template needs the version it was made on');
        }
        if (content.version !== template.version) {
          throw new ApiError(409, 'ERR_CONFLICT', `template ${template.name} was changed by ${template.updatedBy} (version ${template.version}) after you opened it: open it again and redo your change`);
        }
        const changed = structuredClone(template);
        writeTemplate(changed, content);
        Object.assign(template, changed, { version: template.version + 1 });
        save();
        return templateView(template);
      }),

    /** Backend: TemplateUpdates. Only the operator's choices move; what the customer wrote stays. */
    updateTemplateInstances: async (id) => {
      forOperators('Updating the workspaces of a template');
      const template = findTemplate(id);
      const updated = [];
      const unchanged = [];
      const skipped = [];
      const behindIn = (instance) => Object.entries(instance.templates ?? {}).filter(([, copy]) => copy.templateId === id && copy.templateVersion < template.version);
      for (const instance of data.instances.filter((candidate) => behindIn(candidate).length > 0).sort((a, b) => a.name.localeCompare(b.name))) {
        const desired = data.revisions.find((revision) => revision.id === instance.desiredRevisionId);
        if (!desired) {
          skipped.push({ instance: instance.name, reason: 'it is not on its server: apply one of its revisions first' });
          continue;
        }
        // the rules and the image version are the workspace's: only the template it was created from has them
        const origin = instance.templateId === id;
        const imageVersion = origin ? template.imageVersion : instance.imageVersion;
        const rules = origin ? (template.operatorRules ?? '') : (desired.operatorRules ?? '');
        const params = structuredClone(desired.params);
        const reserved = new Set([
          ...operatorParams(assistantSchema(IMAGE_VERSIONS[instance.imageVersion]?.paramsSchema ?? {}) ?? {}),
          ...operatorParams(assistantSchema(IMAGE_VERSIONS[imageVersion].paramsSchema)),
        ]);
        const copies = behindIn(instance);
        const missing = copies.find(([assistantId]) => !params.assistants[assistantId]);
        if (missing) {
          skipped.push({ instance: instance.name, reason: `its assistant ${missing[0]}, made from the template, is no longer in the workspace` });
          continue;
        }
        for (const [assistantId] of copies) {
          const assistant = params.assistants[assistantId];
          for (const name of reserved) {
            if (name in template.params) {
              assistant[name] = structuredClone(template.params[name]);
            } else {
              delete assistant[name];
            }
          }
        }
        const errors = validate(IMAGE_VERSIONS[imageVersion].paramsSchema, params);
        if (Object.keys(errors).length > 0) {
          const [field, message] = Object.entries(errors)[0];
          skipped.push({ instance: instance.name, reason: `${field}: ${message}` });
          continue;
        }
        for (const [, copy] of copies) {
          copy.templateVersion = template.version;
        }
        if (canonical(params) === canonical(desired.params) && rules === (desired.operatorRules ?? '') && imageVersion === instance.imageVersion) {
          unchanged.push(instance.name);
          continue;
        }
        data.sequence += 1;
        const revision = {
          id: `rev_demo_${data.sequence}`,
          instance: instance.name,
          number: Math.max(...data.revisions.filter((candidate) => candidate.instance === instance.name).map((candidate) => candidate.number)) + 1,
          status: 'published',
          author: author(),
          createdAt: clock(),
          digest: null,
          params,
          personas: structuredClone(desired.personas),
          operatorRules: rules,
        };
        await ensureDigest(revision);
        data.revisions.push(revision);
        instance.imageVersion = imageVersion;
        instance.desiredRevisionId = revision.id;
        changeDesired(instance);
        simulations.delete(instance.name);
        if (isOnline(findServer(instance.serverId))) {
          startApply(instance, null);
        }
        updated.push(instance.name);
      }
      save();
      return respond(() => ({ version: template.version, updated, unchanged, skipped }));
    },

    archiveTemplate: (id, archived) =>
      respond(() => {
        forOperators('Managing templates');
        const template = findTemplate(id);
        template.archivedAt = archived ? (template.archivedAt ?? clock()) : null;
        save();
        return templateView(template);
      }),

    setServerStatus: (id, status) =>
      respond(() => {
        forOperators('Revoking a server');
        const server = findServer(id);
        if (status !== 'active' && status !== 'revoked') {
          throw new ApiError(400, 'ERR_INVALID', 'status must be active or revoked');
        }
        server.status = status;
        if (status === 'revoked') {
          disconnect(server);
        }
        save();
        return serverDetail(server);
      }),

    revokeServerKey: (id) =>
      respond(() => {
        forOperators('Revoking a key');
        const server = findServer(id);
        if (server.connectedSince == null) {
          throw new ApiError(409, 'ERR_CONFLICT', `server ${id} has never connected: there is no key to revoke`);
        }
        server.keyRevoked = true;
        disconnect(server);
        save();
        return serverDetail(server);
      }),

    takeOverServer: (id) =>
      respond(() => {
        forOperators('Taking a server over');
        const server = findServer(id);
        if (server.aheadGeneration == null) {
          throw new ApiError(409, 'ERR_CONFLICT', `server ${id} is not held: its clawlet is in step with the plane`);
        }
        server.desiredGeneration = Math.max(server.desiredGeneration, server.aheadGeneration, server.clawletGeneration ?? 0) + 1;
        server.aheadGeneration = null;
        server.holdReason = null;
        // the instances the plane does not know are removed by the clawlet
        server.unknownInstances = [];
        if (isOnline(server)) {
          server.ackedGeneration = server.desiredGeneration;
          reconcile(server);
        }
        save();
        return serverDetail(server);
      }),

    setExpectedNetworks: (id, networks) =>
      respond(() => {
        forOperators('Changing the networks of a server');
        const server = findServer(id);
        const checked = [...new Set((networks ?? []).map((network) => network.trim()).filter(Boolean))];
        const wrong = checked.find((network) => !isNetwork(network));
        if (wrong) {
          throw new ApiError(400, 'ERR_INVALID', `not an IP address or a CIDR block: ${wrong}`);
        }
        server.expectedNetworks = checked;
        save();
        return serverDetail(server);
      }),

    enrollmentRequests: () =>
      respond(() => {
        forOperators('The enrollment requests');
        return data.enrollmentRequests.filter((request) => request.status === 'pending').map(enrollmentRequestView);
      }),

    /**
     * Backend: EnrollmentRequests.approve. The demo clawlet picks up its certificate and connects
     * a moment later; the previous key of an existing server is revoked.
     */
    approveEnrollmentRequest: (id, { serverId = null, name = null, rememberAddress = false } = {}) =>
      respond(() => {
        forOperators('Approving an enrollment request');
        const request = findPendingRequest(id);
        const target = serverId?.trim() || request.requestedServerId;
        if (!target) {
          throw new ApiError(400, 'ERR_INVALID', 'the request names no server: choose the server it is for');
        }
        const server = data.servers.find((candidate) => candidate.id === target) ?? addServer(target, name?.trim() || target);
        if (server.status !== 'active') {
          throw new ApiError(403, 'ERR_FORBIDDEN', 'server revoked');
        }
        disconnect(server);
        request.status = 'approved';
        server.keyRevoked = false;
        server.certificateExpiresAt = clock() + 90 * 24 * 60 * 60 * 1000;
        if (rememberAddress && !inNetworks(server.expectedNetworks ?? [], request.sourceAddress)) {
          server.expectedNetworks = [...(server.expectedNetworks ?? []), request.sourceAddress];
        }
        later(stepMs * 3, () => {
          server.connectedSince = clock();
          server.disconnectedAt = null;
          server.helloAt = clock();
          server.lastAddress = request.sourceAddress;
          server.clawletVersion ??= '0.4.2';
          if (server.imageVersions.length === 0) {
            server.imageVersions = ['2026.10.4', '2026.10.8'];
          }
          reconcile(server);
          save();
        });
        save();
        return serverDetail(server);
      }),

    denyEnrollmentRequest: (id) =>
      respond(() => {
        forOperators('Denying an enrollment request');
        findPendingRequest(id).status = 'denied';
        save();
        return data.enrollmentRequests.filter((request) => request.status === 'pending').map(enrollmentRequestView);
      }),

    newEnrollmentToken: (id) =>
      respond(() => {
        forOperators('An enrollment token');
        const server = findServer(id);
        if (server.status !== 'active') {
          throw new ApiError(409, 'ERR_CONFLICT', `server ${id} is revoked: make it active before enrolling it`);
        }
        const issued = issueToken(server);
        // the clawlet that enrolls with it gets a new key
        server.keyRevoked = false;
        save();
        return issued;
      }),

    createCustomer: (name) =>
      respond(() => {
        forOperators('Managing customers');
        const trimmed = (name ?? '').trim();
        if (!trimmed) {
          throw new ApiError(400, 'ERR_INVALID', 'a customer needs a name of 1 to 200 characters');
        }
        if (data.customers.some((customer) => customer.name === trimmed)) {
          throw new ApiError(409, 'ERR_CONFLICT', `customer ${trimmed} already exists`);
        }
        data.sequence += 1;
        const customer = { id: `cus_demo_${data.sequence}`, name: trimmed, users: [] };
        data.customers.push(customer);
        save();
        return customerView(customer);
      }),

    customerMembers: (customerId) => respond(() => memberViews(seenCustomer(customerId))),

    addCustomerUser: (customerId, username, role = 'member') =>
      respond(() => {
        forOperators('Adding an existing user');
        const customer = managedCustomer(customerId);
        if (!/^[\w.@-]{2,64}$/.test(username ?? '')) {
          throw new ApiError(404, 'ERR_NOT_FOUND', `user ${username} not found`);
        }
        if (!customer.users.includes(username)) {
          customer.owners = ownersOf(customer);
          customer.users.push(username);
        }
        setRole(customer, username, role);
        save();
        return customerView(customer);
      }),

    changeCustomerUserRole: (customerId, username, role) =>
      respond(() => {
        const customer = managedCustomer(customerId);
        if (!customer.users.includes(username)) {
          throw notFound(`Member ${username}`);
        }
        setRole(customer, username, role);
        save();
        return customerView(customer);
      }),

    removeCustomerUser: (customerId, username) =>
      respond(() => {
        const customer = managedCustomer(customerId);
        const owners = ownersOf(customer);
        if (asCustomer() && owners.length === 1 && owners[0] === username) {
          throw new ApiError(409, 'ERR_CONFLICT', 'The organization must keep at least one owner');
        }
        customer.owners = owners.filter((owner) => owner !== username);
        customer.users = customer.users.filter((user) => user !== username);
        save();
        return customerView(customer);
      }),

    inviteToCustomer: (customerId, { role = 'member' } = {}) =>
      respond(() => {
        const customer = managedCustomer(customerId);
        if (role !== 'owner' && role !== 'member') {
          throw new ApiError(400, 'ERR_INVALID', `unknown role ${role}: owner or member`);
        }
        data.sequence += 1;
        const createdAt = new Date(clock()).toISOString();
        const invitation = {
          id: `inv_demo_${data.sequence}`,
          role,
          invitedBy: author(),
          createdAt,
          expiresAt: new Date(clock() + 7 * 24 * 3600 * 1000).toISOString(),
        };
        customer.invitations = [invitation, ...(customer.invitations ?? [])];
        save();
        // the demo has no invitation pages: the link only shows what the real one looks like
        const origin = globalThis.location?.origin ?? 'http://localhost:8080';
        // the demo sends no email
        return { ...invitation, url: `${origin}/-/app/invitations/demo${data.sequence}`, emailSent: false };
      }),

    customerInvitations: (customerId) => respond(() => [...(managedCustomer(customerId).invitations ?? [])]),

    revokeCustomerInvitation: (customerId, invitationId) =>
      respond(() => {
        const customer = managedCustomer(customerId);
        if (!(customer.invitations ?? []).some((invitation) => invitation.id === invitationId)) {
          throw notFound(`Invitation ${invitationId}`);
        }
        customer.invitations = customer.invitations.filter((invitation) => invitation.id !== invitationId);
        save();
        return invitationId;
      }),

    assignCustomer: (name, customerId) =>
      respond(() => {
        forOperators('Managing customers');
        const instance = findInstance(name);
        instance.customer = customerId == null ? null : findCustomer(customerId).name;
        save();
        return instanceView(instance);
      }),

    imageVersion: (version) =>
      respond(() => {
        const found = IMAGE_VERSIONS[version];
        if (!found) {
          throw notFound(`Image version ${version}`);
        }
        return found;
      }),

    /** Controls of the demo itself, shown by <plane-demo-bar>. Not part of the plane API. */
    demo: {
      failureModes: FAILURE_MODES,
      failNext: () => failNext,
      failNextApply: (mode) => {
        failNext = mode in FAILURE_MODES ? mode : null;
      },
      servers: () => data.servers.map(serverView),
      /** Who the plane is shown as: null for the operator, or a customer name. */
      viewAs: () => data.viewAs,
      customerNames: () => data.customers.map((customer) => customer.name).sort(),
      setViewAs: (customer) => {
        data.viewAs = customer || null;
        save();
      },
      /**
       * Connects or disconnects a server. A disconnection is dated past the grace period, so the
       * server shows as offline right away instead of after a minute.
       */
      setConnected: (id, connected) => {
        const server = findServer(id);
        if (connected) {
          if (server.status !== 'active' || server.keyRevoked) {
            // refused before the WebSocket upgrade (backend: ServerAdmission)
            return;
          }
          server.connectedSince = clock();
          server.disconnectedAt = null;
          server.helloAt = clock();
          reconcile(server);
        } else {
          disconnect(server);
        }
        save();
      },
      /**
       * Makes the clawlet of a server ahead of the plane, as after a plane started again with an
       * empty database: the server is held until it is taken over.
       */
      setAhead: (id, generation, unknownInstances = []) => {
        const server = findServer(id);
        server.clawletGeneration = generation;
        server.aheadGeneration = generation > server.desiredGeneration ? generation : null;
        server.holdReason = server.aheadGeneration != null ? 'ahead' : null;
        server.unknownInstances = unknownInstances;
        save();
      },
      reset: () => {
        for (const timer of timers) {
          clearTimeout(timer);
        }
        timers.clear();
        simulations.clear();
        failNext = null;
        data = initialData(clock());
        save();
      },
    },
  };
  // requests name an instance by name or by id, as the backend accepts: the rest of the demo by name
  for (const method of ['instance', 'revision', 'saveRevision', 'apply', 'removeInstance', 'deleteInstance', 'renameWorkspace', 'restart', 'setSecret', 'assignCustomer', 'allowPairingRequest', 'ignorePairingRequest']) {
    const byName = api[method];
    api[method] = (ref, ...rest) => byName(nameOf(ref), ...rest);
  }
  const eventsByName = api.events;
  api.events = ({ instance, ...rest } = {}) => eventsByName({ ...rest, instance: instance && nameOf(instance) });
  return api;
}
